import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Source, type Asset } from '@reelmachine/core';
import { UPLOAD_TYPES, isAllowedUploadType } from '../api/client';
import { uploadFile, useApi, useCreateReel, useCurrentOrg } from '../api/hooks';
import { Icon } from '../ui/Icon';
import { ErrorView, Loading, Screen } from '../ui/Screen';
import { Segmented } from '../ui/Segmented';
import { useToast } from '../ui/Toast';
import { BATCH_GAP_MS, BATCH_MAX, parseBatch } from './batch';

type Mode = 'link' | 'text' | 'batch';
interface Draft {
  id: number;
  name: string;
  preview: string;
  asset?: Asset;
  failed?: boolean;
}

/** For the file picker; the check in `addFiles` is the real gate (pickers can be bypassed). */
const ACCEPT = [...UPLOAD_TYPES, 'audio/*'].join(',');
export const REJECTED_FILE_MESSAGE = 'Use JPEG, PNG or WebP images, MP4, WebM or MOV videos, or audio files.';

const MODES = [
  { value: 'link', label: 'Link' },
  { value: 'text', label: 'Paste text' },
  { value: 'batch', label: 'Several' },
] as const;

export function NewReel() {
  const navigate = useNavigate();
  const toast = useToast();
  const api = useApi();
  const { orgId, me } = useCurrentOrg();
  const create = useCreateReel(orgId ?? '');
  const [mode, setMode] = useState<Mode>('link');
  const [link, setLink] = useState('');
  const [text, setText] = useState('');
  const [batch, setBatch] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);
  const urls = useRef<string[]>([]);

  useEffect(() => () => urls.current.forEach((u) => URL.revokeObjectURL?.(u)), []);

  if (me.isError) return <ErrorView title="New reel" back="/" error={me.error} />;
  if (!orgId) return <Loading title="New reel" />;

  const uploading = drafts.some((d) => !d.asset && !d.failed);
  const batchCount = mode === 'batch' ? parseBatch(batch).length : 0;

  const addFiles = (files: FileList | null) => {
    const all = Array.from(files ?? []);
    const rejected = all.filter((f) => !isAllowedUploadType(f.type));
    setError(
      rejected.length
        ? `${rejected.map((f) => f.name).join(', ')} ${rejected.length === 1 ? 'is' : 'are'} not supported. ${REJECTED_FILE_MESSAGE}`
        : null,
    );
    for (const file of all.filter((f) => isAllowedUploadType(f.type))) {
      seq.current += 1;
      const id = seq.current;
      const preview = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : '';
      if (preview) urls.current.push(preview);
      setDrafts((d) => [...d, { id, name: file.name, preview }]);
      const contentType = file.type;
      uploadFile(api, orgId, { kind: 'asset', contentType, name: file.name }, file)
        .then((up) =>
          setDrafts((d) =>
            d.map((x) => (x.id === id ? { ...x, asset: { key: up.key, name: file.name, contentType } } : x)),
          ),
        )
        .catch(() => {
          setDrafts((d) => d.map((x) => (x.id === id ? { ...x, failed: true } : x)));
          toast(`Could not upload ${file.name}`);
        });
    }
  };

  const generateBatch = async () => {
    setError(null);
    const sources = parseBatch(batch);
    if (!sources.length) {
      setError('No links found. Paste links, or texts separated by a line with ---.');
      return;
    }
    if (sources.length > BATCH_MAX) {
      setError(`At most ${BATCH_MAX} reels at once. Found ${sources.length}.`);
      return;
    }
    const assets = drafts.flatMap((d) => (d.asset ? [d.asset] : []));
    const failed: string[] = [];
    setProgress({ done: 0, total: sources.length });
    for (const [i, source] of sources.entries()) {
      if (i > 0) await new Promise((r) => setTimeout(r, BATCH_GAP_MS));
      try {
        await create.mutateAsync({ source, assets });
      } catch {
        failed.push(source.type === 'link' ? source.value : source.value.slice(0, 40));
      }
      setProgress({ done: i + 1, total: sources.length });
    }
    setProgress(null);
    toast(
      failed.length
        ? `${failed.length} could not be started: ${failed.join(', ')}`
        : `${sources.length} reels started.`,
    );
    navigate('/');
  };

  const generate = async () => {
    if (mode === 'batch') return generateBatch();
    setError(null);
    const value = (mode === 'link' ? link : text).trim();
    if (!value) {
      setError('Add a link or text first.');
      return;
    }
    const source = Source.safeParse({ type: mode, value });
    if (!source.success) {
      setError(
        mode === 'link'
          ? 'Enter a full link, starting with https://'
          : 'The text is too short. Paste at least a paragraph.',
      );
      return;
    }
    try {
      const assets = drafts.flatMap((d) => (d.asset ? [d.asset] : []));
      const res = await create.mutateAsync({ source: source.data, assets });
      navigate(`/reels/${res.reel.id}/script`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the script.');
    }
  };

  return (
    <Screen
      title="New reel"
      back="/"
      bar={
        <button
          type="button"
          className="btn"
          disabled={create.isPending || uploading || !!progress}
          onClick={() => void generate()}
        >
          {progress
            ? `Starting ${Math.min(progress.done + 1, progress.total)} of ${progress.total}…`
            : create.isPending
              ? 'Starting…'
              : mode === 'batch'
                ? `Generate ${batchCount} scripts`
                : 'Generate script'}
        </button>
      }
      barHint={
        uploading
          ? 'Uploading images…'
          : progress
            ? 'Keep this page open. One reel starts every 20 seconds.'
            : undefined
      }
    >
      <Segmented label="Source" value={mode} options={MODES} onChange={setMode} />
      {mode === 'batch' ? (
        <>
          <textarea
            rows={10}
            aria-label="Several sources"
            placeholder={'One link per line, or texts separated by a line with ---'}
            value={batch}
            disabled={!!progress}
            onChange={(e) => setBatch(e.target.value)}
          />
          <p className="muted small">
            {batchCount ? `${batchCount} reels. ` : ''}Each one gets its own script. Images below go into every reel.
          </p>
        </>
      ) : mode === 'link' ? (
        <input
          type="url"
          inputMode="url"
          aria-label="Article URL"
          placeholder="https://"
          value={link}
          onChange={(e) => setLink(e.target.value)}
        />
      ) : (
        <textarea
          rows={8}
          aria-label="Source text"
          placeholder="Paste the article or press release"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      )}
      {error && (
        <p className="error-text" role="alert">
          {error}
        </p>
      )}
      <div className="card">
        <span className="label">Images</span>
        <p className="muted small">A photo of a person from the story goes into the hook.</p>
        <div className="assets">
          {drafts.map((d) => (
            <div
              key={d.id}
              className={`asset ${!d.asset && !d.failed ? 'busy' : ''}`}
              title={d.failed ? `${d.name}: upload failed` : d.name}
            >
              {d.preview ? <img src={d.preview} alt={d.name} /> : d.name}
            </div>
          ))}
          <label
            className="asset upload"
            htmlFor="file"
            tabIndex={0}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && document.getElementById('file')?.click()}
          >
            <Icon name="plus" />
            Add
          </label>
        </div>
        <input
          id="file"
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          aria-label="Add images"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
    </Screen>
  );
}
